import { AsyncStatus, useConfirm } from '@frontend/mui'
import { PriorityHigh } from '@mui/icons-material'
import { Button, Grid, Stack, Typography } from '@mui/material'
import { useCallback } from 'react'
import { useEffectOnce } from 'react-use'
import { useRecoilState } from 'recoil'
import { logout } from 'src/context/apollo'
import {
  bindWechatStaffMutation,
  unbindWechatStaffMutation,
} from 'src/generated/apollo'
import { accessTokenStorage, ssoStorage, wxMPState } from './useWxMP'
import { useIntl } from '@frontend/t7nd'

export const WxMPBindMessages = {
  BIND_FAILED: {
    id: '绑定失败',
    defaultMessage: '绑定失败',
  },
  PLEASE_REBIND: {
    id: '请重新绑定',
    defaultMessage: '请重新绑定',
  },
  UNBIND_FAILED: {
    id: '取消绑定失败',
    defaultMessage: '取消绑定失败',
  },
  CONFIRM_UNBIND: {
    id: '确定取消绑定吗？',
    defaultMessage: '确定取消绑定吗？',
  },
  RETURN: {
    id: '返回',
    defaultMessage: '返回',
  },
  CONFIRM_CANCEL: {
    id: '确定取消',
    defaultMessage: '确定取消',
  },
  REBIND: {
    id: '重新绑定',
    defaultMessage: '重新绑定',
  },
} as const

export function WxMPBindFailedPage() {
  const { formatMessage } = useIntl()
  const confirm = useConfirm()
  const [wxMP, setWxMP] = useRecoilState(wxMPState)
  const accessToken = accessTokenStorage.get()
  const { bindStatus } = wxMP

  const bind = useCallback(async () => {
    if (!accessToken) {
      setWxMP((prev) => ({
        ...prev,
        bindStatus: 'invalid',
      }))
      return
    }

    const { data } = await bindWechatStaffMutation({
      variables: { accessToken },
    })

    if (data?.bindWechatStaff) {
      ssoStorage.remove()
      setWxMP((prev) => ({
        ...prev,
        bindStatus: 'success',
        isBindSuccess: true,
      }))
    } else {
      setWxMP((prev) => ({
        ...prev,
        bindStatus: 'invalid',
      }))
    }
  }, [accessToken, setWxMP])

  const unbind = useCallback(async () => {
    const { data } = await unbindWechatStaffMutation()

    if (!data?.unbindWechatStaff) {
      await confirm({ title: formatMessage(WxMPBindMessages.UNBIND_FAILED) })
    }
    setWxMP({
      isBindSuccess: false,
      isAuthorized: false,
      bindStatus: null,
    })
    logout()
  }, [confirm, setWxMP, formatMessage])

  const onClickButton = useCallback(async () => {
    if (bindStatus === 'success') {
      const confirmed = await confirm({
        title: formatMessage(WxMPBindMessages.CONFIRM_UNBIND),
        cancelLabel: formatMessage(WxMPBindMessages.RETURN),
        confirmLabel: formatMessage(WxMPBindMessages.CONFIRM_CANCEL),
      })
      if (confirmed) {
        unbind()
      }
    } else {
      unbind()
    }
  }, [bindStatus, confirm, unbind])

  useEffectOnce(() => {
    if (!bindStatus) {
      bind()
    }
  })

  return (
    <AsyncStatus loading={!bindStatus}>
      <Stack sx={{ height: window.innerHeight }}>
        <Stack
          rowGap="10px"
          alignItems="center"
          justifyContent="center"
          height={700}
        >
          <Grid
            sx={{
              display: 'grid',
              width: '80px',
              height: '80px',
              borderRadius: '100%',
              background: '#f4664a',
              placeItems: 'center',
              color: 'white',
              fontSize: '48px',
              marginBottom: '14px',
            }}
          >
            <PriorityHigh fontSize="inherit" />
          </Grid>
          <Typography variant="h4">
            {formatMessage(WxMPBindMessages.BIND_FAILED)}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            whiteSpace="pre-wrap"
          >
            {formatMessage(WxMPBindMessages.PLEASE_REBIND)}
          </Typography>
        </Stack>
        <Button onClick={() => onClickButton()} variant="text">
          {formatMessage(WxMPBindMessages.REBIND)}
        </Button>
      </Stack>
    </AsyncStatus>
  )
}
